import flightsData from '../data/flights.json';
import airlinesData from '../data/airlines.json';
import citiesData from '../data/cities.json';

/**
 * Extract airport codes from city objects
 * @param {Array} cities - Array of city objects from autocomplete
 * @returns {Array} Array of airport codes
 */
export const extractAirportCodes = (cities) => {
  if (!cities || cities.length === 0) {
    return [];
  }
  
  const codes = cities.map(city => {
    // Handle city objects from autocomplete
    if (typeof city === 'object' && city.code) {
      return city.code;
    }
    // Handle string format "City (CODE)" as fallback
    if (typeof city === 'string') {
      const match = city.match(/\(([A-Z]{3})\)/);
      return match ? match[1] : null;
    }
    return null;
  }).filter(code => code !== null);
  
  return codes;
};

/**
 * Generate dynamic flight search results from existing data files
 * @param {string} from - Departure airport code
 * @param {string} to - Arrival airport code  
 * @param {string} date - Search date (YYYY-MM-DD)
 * @param {Object} options - Additional search options
 * @returns {Object} Search result object
 */
export const generateDynamicSearchResults = (from, to, date, options = {}) => {
  // Find matching flights
  const matchingFlights = flightsData.filter(flight => 
    flight.departure.airport === from && 
    flight.arrival.airport === to
  );

  if (matchingFlights.length === 0) {
    return {
      id: `SR_${Date.now()}`,
      searchId: `${from}-${to}-${date}`,
      from,
      to,
      date,
      results: [],
      totalResults: 0,
      message: "No flights found for this route"
    };
  }

  // Filter by date if provided
  let filteredFlights = matchingFlights;
  if (date) {
    const searchDate = new Date(date);
    const dayOfWeek = searchDate.toLocaleDateString('en-US', { weekday: 'long' });
    filteredFlights = matchingFlights.filter(flight => 
      flight.operatingDays.includes(dayOfWeek)
    );
  }

  // Apply additional filters
  if (options.airlines && options.airlines.length > 0) {
    filteredFlights = filteredFlights.filter(flight => 
      options.airlines.includes(flight.airline)
    );
  }

  if (options.stops !== undefined) {
    filteredFlights = filteredFlights.filter(flight => 
      flight.stops === options.stops
    );
  }

  if (options.maxPrice) {
    filteredFlights = filteredFlights.filter(flight => 
      flight.price.economy <= options.maxPrice
    );
  }

  // Enhance flight results with airline and city data
  const enhancedResults = filteredFlights.map(flight => {
    const airline = airlinesData.find(a => a.code === flight.airline);
    const departureCity = citiesData.find(c => c.code === flight.departure.airport);
    const arrivalCity = citiesData.find(c => c.code === flight.arrival.airport);

    return {
      id: `RES_${flight.id}`,
      airline: flight.airline,
      airlineName: flight.airlineName,
      airlineLogo: airline?.logo || null,
      flightNumber: flight.flightNumber,
      departure: {
        airport: flight.departure.airport,
        city: flight.departure.city,
        time: flight.departure.time,
        terminal: flight.departure.terminal
      },
      arrival: {
        airport: flight.arrival.airport,
        city: flight.arrival.city,
        time: flight.arrival.time,
        terminal: flight.arrival.terminal
      },
      duration: flight.duration,
      stops: flight.stops,
      layover: flight.layover,
      aircraft: flight.aircraft,
      cabinClass: flight.cabinClass,
      price: flight.price,
      baggage: flight.baggage,
      amenities: flight.amenities,
      bookingUrl: airline?.bookingUrl || `https://www.${flight.airline.toLowerCase()}.com`,
      isDirect: flight.stops === 0,
      quality: airline?.isLowCost ? "Budget" : "Premium"
    };
  });

  // Sort results by price (cheapest first)
  enhancedResults.sort((a, b) => a.price.economy - b.price.economy);

  return {
    id: `SR_${Date.now()}`,
    searchId: `${from}-${to}-${date}`,
    from,
    to,
    date,
    results: enhancedResults,
    totalResults: enhancedResults.length,
    searchOptions: options
  };
};

/**
 * Generate multiple search results for different routes
 * @param {Array} routes - Array of route objects {from, to, date}
 * @returns {Array} Array of search result objects
 */
export const generateMultipleSearchResults = (routes) => {
  return routes.map(route => 
    generateDynamicSearchResults(route.from, route.to, route.date, route.options)
  );
};

/**
 * Generate search results with random variations
 * @param {string} from - Departure airport code
 * @param {string} to - Arrival airport code
 * @param {string} date - Search date
 * @returns {Object} Search result with random price variations
 */
export const generateRandomizedSearchResults = (from, to, date) => {
  const baseResults = generateDynamicSearchResults(from, to, date);
  
  // Add random price variations (±20%)
  const randomizedResults = baseResults.results.map(flight => {
    const priceVariation = 0.8 + Math.random() * 0.4; // ±20%
    return {
      ...flight,
      price: {
        economy: Math.round(flight.price.economy * priceVariation),
        premiumEconomy: flight.price.premiumEconomy ? 
          Math.round(flight.price.premiumEconomy * priceVariation) : null,
        business: flight.price.business ? 
          Math.round(flight.price.business * priceVariation) : null
      }
    };
  });

  return {
    ...baseResults,
    results: randomizedResults,
    searchId: `${from}-${to}-${date}-${Date.now()}` // Add timestamp for uniqueness
  };
};

/**
 * Get available routes from flights data
 * @returns {Array} Array of unique routes
 */
export const getAvailableRoutes = () => {
  const routes = new Set();
  
  flightsData.forEach(flight => {
    const route = `${flight.departure.airport}-${flight.arrival.airport}`;
    routes.add(route);
  });

  return Array.from(routes).map(route => {
    const [from, to] = route.split('-');
    return { from, to };
  });
};

/**
 * Search flights with advanced filtering
 * @param {Object} searchCriteria - Search parameters
 * @returns {Array} Array of flight results in the format expected by filters
 */
export const searchFlights = (searchCriteria) => {
  const {
    from = [],
    to = [],
    departureDate,
    returnDate,
    tripType = 'One-way',
    cabin = 'Economy',
    adults = 1,
    maxPrice,
    airlines,
    stops,
    amenities
  } = searchCriteria;

  // Extract airport codes from city selections
  const fromAirports = extractAirportCodes(from);
  const toAirports = extractAirportCodes(to);

  // Get all possible airport codes for metro areas
  const getAllAirportCodes = (cityCodes) => {
    const allCodes = [];
    cityCodes.forEach(code => {
      const city = citiesData.find(city => city.code === code);
      
      if (city && city.airports) {
        // This is a metro area, add all its airports
        allCodes.push(...city.airports);
      } else {
        // This is an individual airport
        allCodes.push(code);
      }
    });
    
    return allCodes;
  };

  const fromAllAirports = getAllAirportCodes(fromAirports);
  const toAllAirports = getAllAirportCodes(toAirports);

  // Filter flights based on departure and arrival airports
  let matchingFlights = flightsData.filter(flight => {
    const matchesFrom = fromAllAirports.includes(flight.departure.airport);
    const matchesTo = toAllAirports.includes(flight.arrival.airport);
    return matchesFrom && matchesTo;
  });

  // Filter by cabin class if specified
  if (cabin && cabin !== "Economy") {
    matchingFlights = matchingFlights.filter(flight => 
      flight.cabinClass === cabin
    );
  }

  // Filter by date if specified (check if flight operates on the given day)
  if (departureDate) {
    const searchDate = new Date(departureDate);
    const dayOfWeek = searchDate.toLocaleDateString('en-US', { weekday: 'long' });
    
    matchingFlights = matchingFlights.filter(flight => 
      flight.operatingDays.includes(dayOfWeek)
    );
  }

  // Create flight combinations for round-trip
  const flightResults = [];
  
  if (tripType === "Round-trip" && returnDate) {
    // For round-trip, create combinations of outbound and return flights
    const outboundFlights = matchingFlights;
    const returnFlights = flightsData.filter(flight => {
      const matchesFrom = toAllAirports.includes(flight.departure.airport);
      const matchesTo = fromAllAirports.includes(flight.arrival.airport);
      
      if (!matchesFrom || !matchesTo) return false;
      
      // Check return date
      const searchDate = new Date(returnDate);
      const dayOfWeek = searchDate.toLocaleDateString('en-US', { weekday: 'long' });
      return flight.operatingDays.includes(dayOfWeek);
    });

    // Create combinations
    outboundFlights.forEach(outbound => {
      returnFlights.forEach(returnFlight => {
        const totalPrice = outbound.price.economy + returnFlight.price.economy;
        
        flightResults.push({
          outbound: {
            ...outbound,
            airlineImg: getAirlineLogo(outbound.airline)
          },
          returnFlights: [{
            ...returnFlight,
            airlineImg: getAirlineLogo(returnFlight.airline)
          }],
          totalPrice
        });
      });
    });
  } else {
    // For one-way trips, just use outbound flights
    matchingFlights.forEach(flight => {
      flightResults.push({
        outbound: {
          ...flight,
          airlineImg: getAirlineLogo(flight.airline)
        },
        returnFlights: [],
        totalPrice: flight.price.economy
      });
    });
  }

  // Sort by price (cheapest first)
  flightResults.sort((a, b) => a.totalPrice - b.totalPrice);

  return flightResults;
};

/**
 * Get airline logo by airline code
 * @param {string} airlineCode - Airline code
 * @returns {string} Logo URL
 */
export const getAirlineLogo = (airlineCode) => {
  const airline = airlinesData.find(airline => airline.code === airlineCode);
  return airline ? airline.logo : null;
};

/**
 * Get airline name by airline code
 * @param {string} airlineCode - Airline code
 * @returns {string} Airline name
 */
export const getAirlineName = (airlineCode) => {
  const airline = airlinesData.find(airline => airline.code === airlineCode);
  return airline ? airline.name : airlineCode;
};

/**
 * Format price with currency
 * @param {number} price - Price in GBP
 * @returns {string} Formatted price
 */
export const formatPrice = (price) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(price);
};

/**
 * Get flight duration in minutes
 * @param {string} duration - Duration string like "1h 15m"
 * @returns {number} Duration in minutes
 */
export const getDurationInMinutes = (duration) => {
  const match = duration.match(/(\d+)h\s*(\d+)?m?/);
  if (match) {
    const hours = parseInt(match[1]);
    const minutes = match[2] ? parseInt(match[2]) : 0;
    return hours * 60 + minutes;
  }
  return 0;
};

/**
 * Sort flights by different criteria
 * @param {Array} flights - Array of flight results
 * @param {string} sortBy - Sort criteria ('price', 'duration', 'departure')
 * @param {boolean} ascending - Sort order
 * @returns {Array} Sorted flights
 */
export const sortFlights = (flights, sortBy = 'price', ascending = true) => {
  return [...flights].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'price':
        comparison = a.totalPrice - b.totalPrice;
        break;
      case 'duration':
        const durationA = getDurationInMinutes(a.outbound.duration);
        const durationB = getDurationInMinutes(b.outbound.duration);
        comparison = durationA - durationB;
        break;
      case 'departure':
        const timeA = new Date(`2000-01-01 ${a.outbound.departure.time}`);
        const timeB = new Date(`2000-01-01 ${b.outbound.departure.time}`);
        comparison = timeA - timeB;
        break;
      default:
        comparison = 0;
    }
    
    return ascending ? comparison : -comparison;
  });
};

// Date and Calendar Utility Functions (needed for date picker)

// Helper: format date as e.g. 'Fri 7/25'
export const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
};

// Calendar logic for DatePickerPopover
export const generateCalendarDates = (month) => {
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const dates = [];
  // Pad start with nulls if the first day is not Sunday
  for (let i = 0; i < firstDay.getDay(); i++) {
    dates.push(null);
  }
  // Add all days of the month
  for (let d = 1; d <= lastDay.getDate(); d++) {
    dates.push(new Date(month.getFullYear(), month.getMonth(), d));
  }
  // Pad end with nulls if the last day is not Saturday
  for (let i = lastDay.getDay(); i < 6; i++) {
    dates.push(null);
  }
  return dates;
};

// Example: Generate a consistent price for each date across months
export const generatePriceForDate = (date) => {
  const day = date.getDate();
  const month = date.getMonth();
  
  // Create a consistent hash based on day and month
  const hash = (day * 31 + month * 13) % 100;
  
  if (hash < 20) return 1472; // Higher prices (20% of dates) - Pink
  if (hash < 50) return 1302; // Medium prices (30% of dates) - Orange
  return 1150; // Lower prices (50% of dates) - Green
};

export const getPriceColor = (price) => {
  if (price >= 290) return ' #e91e63';
  if (price >= 179) return ' #ff9800';
  return 'rgb(13, 206, 19)';
};

export const getPriceBackgroundColor = (price) => {
  if (price >= 290) return ' #e91e63';
  if (price >= 179) return ' #ff9800';
  return 'rgb(69, 191, 16)';
};

// Passenger Display Utilities
export const getTotalPassengers = (adults, students, seniors, youths, children, toddlers, infants) => {
  return adults + students + seniors + youths + children + toddlers + infants;
};

export const getPassengerDisplayText = (adults, students, seniors, youths, children, toddlers, infants) => {
  const total = getTotalPassengers(adults, students, seniors, youths, children, toddlers, infants);
  if (total === 1 && adults === 1) {
    return `1 adult`;
  }
  return `${total} Traveler${total !== 1 ? 's' : ''}`;
}; 